import { useEffect, useState } from "react";
import { notifyAttendance } from "../../utils/notifications";
import { collection, getDocs, addDoc, query, where, serverTimestamp, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import toast from "react-hot-toast";
import { ClipboardList, CheckCircle, XCircle, Save, Plus, Calendar, Users } from "lucide-react";

export default function MarkAttendance() {
  const [members, setMembers] = useState([]);
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState("");
  const [attendance, setAttendance] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showNewEvent, setShowNewEvent] = useState(false);
  const [newEvent, setNewEvent] = useState({ title:"", date:"", location:"", type:"drive" });

  
  useEffect(() => {
    async function load() {
      try {
        const membersSnap = await getDocs(query(collection(db,"users"), orderBy("name")));
        const eventsSnap  = await getDocs(query(collection(db,"events"), orderBy("date","desc")));
        setMembers(membersSnap.docs.map(d=>({id:d.id,...d.data()})));
        setEvents(eventsSnap.docs.map(d=>({id:d.id,...d.data()})));
      } catch(e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, []);

  // When event selected, load existing attendance
  useEffect(() => {
    async function loadAttendance() {
      if (!selectedEvent) return;
      try {
        const q = query(collection(db,"attendance"), where("eventId","==",selectedEvent));
        const snap = await getDocs(q);
        const map = {};
        snap.docs.forEach(d => { map[d.data().userId] = d.data().status; });
        setAttendance(map);
      } catch(e) { console.error(e); }
    }
    loadAttendance();
  }, [selectedEvent]);

  function toggle(uid) {
    setAttendance(prev => ({
      ...prev,
      [uid]: prev[uid] === "present" ? "absent" : "present"
    }));
  }

  function markAll(status) {
    const map = {};
    members.forEach(m => { map[m.id] = status; });
    setAttendance(map);
  }

  async function handleSave() {
    if (!selectedEvent) return toast.error("Please select an event first");
    setSaving(true);
    try {
      const evt = events.find(e=>e.id===selectedEvent);
      const promises = members.map(async m => {
        const status = attendance[m.id] || "absent";
        // Check if record exists
        const existing = await getDocs(query(collection(db,"attendance"),
          where("userId","==",m.id),
          where("eventId","==",selectedEvent)
        ));
        if (existing.empty) {
          await addDoc(collection(db,"attendance"), {
            userId: m.id,
            eventId: selectedEvent,
            eventName: evt?.title || "Event",
            date: evt?.date || new Date().toISOString().split("T")[0],
            status,
            markedAt: serverTimestamp(),
          });
        }
      });
      await Promise.all(promises);
      await notifyAttendance(evt?.title || "Event", "Admin");
      toast.success("Attendance saved successfully!");
    } catch(e) {
      console.error(e);
      toast.error("Failed to save attendance");
    } finally { setSaving(false); }
  }

  async function handleCreateEvent() {
    if (!newEvent.title || !newEvent.date) return toast.error("Title and date required");
    try {
      const ref = await addDoc(collection(db,"events"), {
        ...newEvent,
        createdAt: serverTimestamp(),
      });
      const created = { id:ref.id, ...newEvent };
      setEvents(prev=>[created,...prev]);
      setSelectedEvent(ref.id);
      setNewEvent({ title:"", date:"", location:"", type:"drive" });
      setShowNewEvent(false);
      toast.success("Event created!");
    } catch(e) {
      toast.error("Failed to create event");
    }
  }

  const presentCount = Object.values(attendance).filter(v=>v==="present").length;
  const absentCount  = Object.values(attendance).filter(v=>v==="absent").length;
  const selectedEvt  = events.find(e=>e.id===selectedEvent);

  return (
    <div>
      {/* Header */}
      <div style={s.header}>
        <ClipboardList size={22} color="#f59e0b"/>
        <div>
          <h2 style={s.title}>Mark Attendance</h2>
          <p style={s.sub}>Select an event and mark attendance for all members</p>
        </div>
      </div>

      <div style={s.grid2}>
        {/* Left - Event Selection */}
        <div>
          <div style={s.card}>
            <div style={s.cardTitle}><Calendar size={15} color="#f59e0b"/> Select Event</div>

            {/* Create Event */}
            <button onClick={()=>setShowNewEvent(o=>!o)} style={s.createBtn}>
              <Plus size={14}/> {showNewEvent ? "Cancel" : "Create New Event"}
            </button>

            {showNewEvent && (
              <div style={s.newEventForm}>
                <input value={newEvent.title} onChange={e=>setNewEvent(p=>({...p,title:e.target.value}))}
                  placeholder="Event title *" style={s.input}/>
                <input value={newEvent.date} onChange={e=>setNewEvent(p=>({...p,date:e.target.value}))}
                  type="date" style={s.input}/>
                <input value={newEvent.location} onChange={e=>setNewEvent(p=>({...p,location:e.target.value}))}
                  placeholder="Location" style={s.input}/>
                <select value={newEvent.type} onChange={e=>setNewEvent(p=>({...p,type:e.target.value}))} style={s.input}>
                  <option value="drive">Community Drive</option>
                  <option value="camp">Camp</option>
                  <option value="meeting">Meeting</option>
                  <option value="plantation">Plantation</option>
                  <option value="education">Education</option>
                </select>
                <button onClick={handleCreateEvent} style={{...s.createBtn, background:"rgba(34,197,94,0.12)", borderColor:"rgba(34,197,94,0.3)", color:"#22c55e"}}>
                  ✓ Save Event
                </button>
              </div>
            )}

            <div style={{maxHeight:380,overflowY:"auto",marginTop:12}}>
              {loading ? <div style={s.empty}>Loading events...</div> :
               events.length === 0 ? <div style={s.empty}>No events yet. Create one above!</div> :
               events.map(evt=>(
                <div key={evt.id} onClick={()=>setSelectedEvent(evt.id)}
                  style={{...s.evtItem, background:selectedEvent===evt.id?"rgba(245,158,11,0.1)":"transparent", borderColor:selectedEvent===evt.id?"rgba(245,158,11,0.3)":"rgba(255,255,255,0.05)"}}>
                  <div style={{fontSize:13,fontWeight:600,color:selectedEvent===evt.id?"#f59e0b":"#fef3e2"}}>{evt.title}</div>
                  <div style={{fontSize:11,color:"#52504d",marginTop:2}}>{evt.date} · {evt.location}</div>
                  <span style={{fontSize:10,background:"rgba(255,255,255,0.06)",color:"#78716c",padding:"2px 7px",borderRadius:50,marginTop:4,display:"inline-block",textTransform:"capitalize"}}>{evt.type}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right - Members */}
        <div>
          {selectedEvent ? (
            <div style={s.card}>
              <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:14,flexWrap:"wrap",gap:8}}>
                <div style={s.cardTitle}><Users size={15} color="#f59e0b"/> Members · {selectedEvt?.title}</div>
                <div style={{display:"flex",gap:6}}>
                  <button onClick={()=>markAll("present")} style={{...s.markBtn,background:"rgba(34,197,94,0.12)",color:"#22c55e",border:"1px solid rgba(34,197,94,0.25)"}}>✓ All Present</button>
                  <button onClick={()=>markAll("absent")} style={{...s.markBtn,background:"rgba(239,68,68,0.12)",color:"#ef4444",border:"1px solid rgba(239,68,68,0.25)"}}>✗ All Absent</button>
                </div>
              </div>

              {/* Summary bar */}
              <div style={s.summaryBar}>
                <div style={{fontSize:12,color:"#22c55e",fontWeight:700}}><CheckCircle size={13} style={{display:"inline",marginRight:4}}/>{presentCount} Present</div>
                <div style={{fontSize:12,color:"#ef4444",fontWeight:700}}><XCircle size={13} style={{display:"inline",marginRight:4}}/>{absentCount} Absent</div>
                <div style={{fontSize:12,color:"#52504d"}}>{members.length - presentCount - absentCount} Unmarked</div>
              </div>

              {/* Member list */}
              <div style={{maxHeight:380,overflowY:"auto"}}>
                {members.map(m => {
                  const status = attendance[m.id];
                  return (
                    <div key={m.id} style={s.memberRow} onClick={()=>toggle(m.id)}>
                      <div style={{...s.memberAvatar, background:status==="present"?"rgba(34,197,94,0.15)":status==="absent"?"rgba(239,68,68,0.15)":"rgba(255,255,255,0.05)", border:`1.5px solid ${status==="present"?"#22c55e":status==="absent"?"#ef4444":"rgba(255,255,255,0.1)"}`}}>
                        {(m.name||"?").charAt(0).toUpperCase()}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:600,color:"#fef3e2",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.name}</div>
                        <div style={{fontSize:11,color:"#52504d"}}>{m.memberId || m.role}</div>
                      </div>
                      <div onClick={e=>{e.stopPropagation();toggle(m.id);}} style={{...s.toggleBtn,
                        background: status==="present"?"rgba(34,197,94,0.15)":status==="absent"?"rgba(239,68,68,0.15)":"rgba(255,255,255,0.05)",
                        border:`1.5px solid ${status==="present"?"rgba(34,197,94,0.4)":status==="absent"?"rgba(239,68,68,0.4)":"rgba(255,255,255,0.08)"}`,
                        color: status==="present"?"#22c55e":status==="absent"?"#ef4444":"#52504d"
                      }}>
                        {status==="present" ? <><CheckCircle size={13}/> Present</> :
                         status==="absent"  ? <><XCircle size={13}/> Absent</> :
                         "Tap to mark"}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Save button */}
              <button onClick={handleSave} disabled={saving} style={{...s.saveBtn, opacity:saving?0.7:1, marginTop:16}}>
                <Save size={16}/> {saving ? "Saving..." : `Save Attendance (${members.length} members)`}
              </button>
            </div>
          ) : (
            <div style={{...s.card, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:300}}>
              <ClipboardList size={48} color="#1a1714" style={{marginBottom:16}}/>
              <div style={{fontSize:14,color:"#52504d",textAlign:"center"}}>Select an event from the left<br/>to start marking attendance</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  header: { display:"flex", alignItems:"center", gap:14, marginBottom:20, background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 },
  title: { fontFamily:"'Playfair Display',serif", fontSize:20, fontWeight:800, color:"#fef3e2", margin:"0 0 4px" },
  sub: { fontSize:13, color:"#78716c", margin:0 },
  grid2: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, alignItems:"start" },
  card: { background:"#111009", border:"1px solid rgba(255,255,255,0.07)", borderRadius:14, padding:18 },
  cardTitle: { fontSize:13, fontWeight:700, color:"#fef3e2", marginBottom:12, display:"flex", alignItems:"center", gap:6 },
  createBtn: { display:"inline-flex", alignItems:"center", gap:6, padding:"8px 14px", background:"rgba(245,158,11,0.1)", border:"1px solid rgba(245,158,11,0.25)", borderRadius:8, color:"#f59e0b", fontSize:12, fontWeight:700, cursor:"pointer", fontFamily:"inherit", width:"100%", justifyContent:"center", marginBottom:4 },
  newEventForm: { background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.06)", borderRadius:10, padding:14, marginTop:10, display:"flex", flexDirection:"column", gap:8 },
  input: { width:"100%", padding:"9px 12px", background:"#0a0908", border:"1px solid rgba(255,255,255,0.08)", borderRadius:8, color:"#fef3e2", fontSize:13, fontFamily:"inherit", outline:"none", boxSizing:"border-box" },
  evtItem: { border:"1px solid", borderRadius:10, padding:"12px 14px", marginBottom:6, cursor:"pointer", transition:"all 0.15s" },
  summaryBar: { display:"flex", gap:16, padding:"10px 14px", background:"rgba(255,255,255,0.03)", borderRadius:9, marginBottom:12, flexWrap:"wrap" },
  memberRow: { display:"flex", alignItems:"center", gap:10, padding:"9px 0", borderBottom:"1px solid rgba(255,255,255,0.04)", cursor:"pointer" },
  memberAvatar: { width:34, height:34, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800, color:"#fef3e2", flexShrink:0, transition:"all 0.2s" },
  toggleBtn: { display:"inline-flex", alignItems:"center", gap:5, padding:"5px 12px", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer", flexShrink:0, transition:"all 0.2s", userSelect:"none" },
  saveBtn: { width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:8, padding:"13px", background:"#f59e0b", color:"#0a0908", border:"none", borderRadius:11, fontSize:14, fontWeight:800, cursor:"pointer", fontFamily:"inherit", transition:"opacity 0.2s" },
  markBtn: { padding:"6px 12px", borderRadius:8, fontSize:11, fontWeight:700, cursor:"pointer", fontFamily:"inherit" },
  empty: { fontSize:13, color:"#52504d", textAlign:"center", padding:"24px 0" },
};
